/**
 * SetSlugModal -- 기존 청첩장에 커스텀 URL(slug) 설정하는 모달
 */

import { useEffect, useRef, useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { apiFetch, getErrorMessage } from '../../lib/api.js'
import { Modal, Input, Button, Text } from '../ui/index.js'
import styles from './CreateInvitationModal.module.css'

interface SetSlugModalProps {
  open: boolean
  onClose: () => void
  invitationId: string
  currentSlug: string | null
  onSaved: () => void
}

function normalizeSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/--+/g, '-')
}

export function SetSlugModal({ open, onClose, invitationId, currentSlug, onSaved }: SetSlugModalProps) {
  const [slug, setSlug] = useState(currentSlug ?? '')
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    if (open) {
      setSlug(currentSlug ?? '')
      setSlugStatus('idle')
      setError('')
      setSubmitting(false)
      setCopied(false)
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [open, currentSlug])

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = normalizeSlug(e.target.value)
    setSlug(normalized)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!normalized || normalized.length < 3) {
      setSlugStatus('idle')
      return
    }

    setSlugStatus('checking')
    debounceRef.current = setTimeout(async () => {
      const res = await apiFetch<{ available: boolean }>(
        `/api/invitations/check-slug?slug=${normalized}&exclude=${invitationId}`,
      )
      if (res.success) {
        setSlugStatus(res.data.available ? 'available' : 'taken')
      } else {
        setSlugStatus('idle')
      }
    }, 300)
  }

  const canSubmit = slug.length >= 3 && !submitting && slugStatus !== 'taken'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    setSubmitting(true)
    setError('')

    const res = await apiFetch(`/api/invitations/${invitationId}`, {
      method: 'PUT',
      body: JSON.stringify({ slug }),
    })

    if (res.success) {
      onSaved()
      onClose()
    } else {
      setError(getErrorMessage(res.error, '슬러그 설정에 실패했습니다'))
      setSubmitting(false)
    }
  }

  const origin = window.location.origin
  const shareUrl = slug.length >= 3 ? `${origin}/inv/${slug}` : ''

  return (
    <Modal open={open} onClose={onClose} title="커스텀 링크 설정" size="sm">
      <form onSubmit={handleSubmit} className={styles.form}>
        <div>
          <Input
            label="커스텀 URL"
            value={slug}
            onChange={handleSlugChange}
            placeholder="minjun-yuna (영문, 숫자, 하이픈만)"
          />
          {shareUrl && (
            <div className={styles.urlRow}>
              <Text variant="caption" style={{ color: 'var(--ds-color-text-tertiary)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {shareUrl}
              </Text>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={(e) => {
                  e.preventDefault()
                  navigator.clipboard.writeText(shareUrl)
                  setCopied(true)
                  setTimeout(() => setCopied(false), 2000)
                }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          )}
          <div className={styles.slugFeedback}>
            {slugStatus === 'checking' && (
              <Text variant="caption" style={{ color: 'var(--ds-color-text-secondary)' }}>
                확인 중...
              </Text>
            )}
            {slugStatus === 'available' && (
              <Text variant="caption" style={{ color: 'var(--ds-color-text-success, #16a34a)' }}>
                사용 가능한 URL입니다
              </Text>
            )}
            {slugStatus === 'taken' && (
              <Text variant="caption" style={{ color: 'var(--ds-color-text-danger, #dc2626)' }}>
                이미 사용 중인 URL입니다
              </Text>
            )}
            <Text variant="caption" style={{ color: 'var(--ds-color-text-tertiary)' }}>
              3~50자, 소문자 영문·숫자·하이픈만 사용 가능
            </Text>
          </div>
        </div>

        {error && (
          <Text variant="caption" style={{ color: 'var(--ds-color-text-danger, #dc2626)' }}>
            {error}
          </Text>
        )}

        <Button type="submit" disabled={!canSubmit}>
          {submitting ? '저장 중...' : '저장'}
        </Button>
      </form>
    </Modal>
  )
}
